/**
 * The Studio's localStorage layer — STUDIO_DESIGN.md 6.2 and 6.4.
 *
 * Owner ruling: localStorage, no new storage mechanism, and `StorageService` stays the
 * writer. It is resolved from `context/container` at CALL time, never with `@inject`, for the
 * reason `utils/metric-emit.ts` gives at length: a class referenced at DECORATION time while
 * its module is mid-evaluation is what TDZ-crashes the SSG build while leaving jest green
 * (`reference_fe_circular_import_tdz`). This module is imported by the model layer's
 * consumers, so it must add no decoration-time edge into the service graph.
 *
 * THREE KEYS, ONE NAMESPACE (6.2). `StorageService` writes `<prefix>-<key>`
 * (`services/storage.service.ts:49`), so with the prefix `STUDIO`:
 *   STUDIO-<projectId>          the document, its clip references, its ui state
 *   STUDIO-LATEST-<pageSlug>    which project this page was last editing
 *   STUDIO-INDEX                the five most recent projects, newest first
 * `projectId` is `[A-Za-z0-9_-]{1,64}`, which admits `INDEX` and `LATEST-…`, so a reserved id
 * is refused at both ends and `newProjectId` cannot mint one (three guards, because the
 * failure is silent data loss).
 *
 * NEVER BYTES (6.4). No `File`, no object URL, no blob, no thumbnail — only the upload's
 * server identity, which is enough to re-fetch a rendition after a reload. A test asserts the
 * serialised record contains neither `blob:` nor `data:`.
 *
 * HONESTY (6.2, "a write-then-read check reports `disabled` so the header chip is honest").
 * Every probe and every write READS BACK what it wrote and compares it. The shipped helpers
 * do not: `StorageService.store` swallows its exception and returns `null` under a
 * `TODO make it better` KDoc (`services/storage.service.ts:47-57`), and
 * `utils/persisted-input.ts:16-23`'s `hasStorage()` writes a probe and REMOVES it without
 * reading it — which answers `true` in the browsers where `setItem` silently succeeds against
 * a zero quota, i.e. exactly the browsers the chip exists for. Quota failures therefore
 * degrade to `{status:'disabled'}`; nothing here throws at the caller.
 *
 * WRITES GO THROUGH THE SERVICE, READS DO NOT. `readRaw`, the read-back inside `writeRaw`
 * and the sweep's key scan touch `window.localStorage` directly, because `StorageService`'s
 * constructor silently falls back to an in-memory object when `localStorage` throws
 * (`storage.service.ts:26-42`) — and reading back out of that object would report `saved` for
 * a write that reached no disk at all. The read-back IS the reason for the split.
 *
 * VERSIONING (6.4). An unknown `v` reads as ABSENT, never as a migration. Unknown keys at the
 * record ROOT survive a save, because the writer spreads what it read; keys INSIDE `doc`
 * cannot, because `doc` is rebuilt from typed editor state on every save (D13) — stated here
 * rather than discovered later.
 *
 * THE UI STATE IS PART OF THE RECORD, not a second store (review I6). `ui.frameTouched` is
 * the frame latch; it is persisted here because it must survive the reload that `doc.frame`
 * survives, and because history snapshots `{doc, ui}` so an undo restores both halves at once.
 *
 * IT REMEMBERS THE LAST SUBMISSION (whole-branch review, I1). `model/composition.ts`'s
 * revision rule is a COMPARISON — `nextRevision(doc, signature, last)` bumps only when the
 * export-affecting fingerprint moved — and `last` is the one input the editor cannot rebuild
 * from the document. Without it on the record: export (revision → 2), keep editing, reload
 * inside the 8 h window, export again, `last` is null, and the SAME `(projectId, revision=2)`
 * is submitted with a DIFFERENT fingerprint, which is the 409 §3.3 warns about — in exactly
 * the flow persistence exists for. So `submission` rides with the record: a restore hands
 * `storedSubmission(record)` to `touchForExport`, and a save stores `built.submission`. It is
 * OPTIONAL, so a record written before this field reads back at `v: 1` unchanged (a `null`
 * `last` is the pre-export behaviour, which is what an older record honestly means).
 */
import { container } from '../../../context/container';
import { StorageService } from '../../../services/storage.service';
import type { OriginalInputFile, ProcessorMetadata } from '../../../types/types';
import { isReservedProjectId, isValidProjectId } from '../model/composition';
import type { ClipAudio, ExportSubmission, StudioDocument, VisualClip } from '../model/composition';

export const STUDIO_PREFIX = 'STUDIO';
export const STUDIO_RECORD_VERSION = 1;

/**
 * The DEFAULTS GENERATION a record was written under — round 5 (L16), orthogonal to `v`.
 *
 * `v` says whether this bundle can read the record at all; a mismatch means "absent" (§6.4).
 * `rules` says which editor defaults the document was born under, so a record this bundle CAN
 * read is brought forward rather than trusted blindly. Generation 1 (rounds 1–3) wrote
 * `audio.mode: 'MUTE'` onto every VIDEO clip the moment a song was added (`studio-tab.component.tsx`
 * before 3cf51076, "adding music mutes VIDEO clips"); generation 2 (round 4, A6) keeps a clip's
 * sound by default and leaves muting to the user. A generation-1 record restored under generation
 * 2 therefore reads as "the user muted these" when nobody did — the owner's "I still cannot hear
 * the video's audio when a song is on the music lane". `migrateRecord` lifts those mutes.
 */
export const STUDIO_RECORD_RULES = 3;

/**
 * How long a project and its uploads are kept — round 8 (O1, S3): **2.5 h**, down from 6.2's 8 h.
 *
 * The owner left a tab open overnight and came back to a timeline whose every rendition answered
 * 404 while the header still promised "files kept a few hours". The edge's retention is the real
 * ceiling and it is shorter than 8 h in practice; 2.5 h is the owner's number. One constant, read
 * by the sweep, the reader, the driver's per-file expiry and the tab's live sweep, so they cannot
 * disagree.
 */
export const STUDIO_TTL_MS = 2.5 * 60 * 60 * 1000;

/** `STUDIO_TTL_MS` as the copy says it: "2.5 hours". */
export const STUDIO_TTL_HOURS = STUDIO_TTL_MS / (60 * 60 * 1000);

/** 6.2: "`STUDIO-INDEX` (<= 5, LRU)". */
export const MAX_INDEXED_PROJECTS = 5;

/** 6.2: "writes debounced 750 ms + `pagehide`". */
export const AUTOSAVE_DEBOUNCE_MS = 750;

const INDEX_KEY = 'INDEX';
const LATEST_PREFIX = 'LATEST-';
/** Round 6 (SDE#8): `STUDIO-INTERRUPTED-<pageSlug>` — the names of uploads a page-leave cut short. */
const INTERRUPTED_PREFIX = 'INTERRUPTED-';

/** Deliberately NOT under `STUDIO-`, so the sweep can never be asked to reason about it. */
const PROBE_KEY = '__xc_studio_probe__';

/** What a clip points at on the server. Never the bytes. */
export interface StoredClipRef {
    clipId: string;
    /**
     * The 24-hex `uploaded-files` id, from `uploadedFileIdOf(location)` (`state/clip-source.ts`).
     *
     * NOT `FileMetadata.getFileIdFromSafeName`, which both these comments used to cite: it exists
     * (`types/types.ts:333-345`, zero callers) but THROWS on a name it cannot read and accepts any
     * 24-character token, so a URL/Dropbox/Drive ingest named `<random>-x-<base>.<ext>` would
     * either crash the editor or mint an id the edge has never heard of. `uploadedFileIdOf`
     * answers null instead, and §6.8's row is what the user sees (P5c.2a D4).
     */
    uploadedFileId: string;
    /** `OriginalInputFile.location` — the stored safe name. */
    safeName: string;
    /** `OriginalInputFile.token` — the upload JWT the rendition route verifies. */
    token: string;
    uploadDomain: string;
    name: string;
    size: number;
    ext: string;
    kind: 'image' | 'video' | 'audio';
    /**
     * A TOMBSTONE: this input slot exists only to keep the later ones in place.
     *
     * `clip.input` indexes the `clips` array, and the editor's own list is append-only, so a file
     * the user removed cannot be dropped from the record without re-pointing every later clip at
     * the wrong file on the next restore. It is kept, and marked — because the alternative, an
     * empty-id reference like the one a file whose UPLOAD expired leaves behind, is read back as
     * "File gone — re-add" and would put a nameless re-add row in the tray, for ever, for a file
     * the user deliberately took out.
     *
     * The two cases are genuinely different and the record has to be able to tell them apart:
     * an expired input is something the user may want to re-add, and a removed one is not.
     * Optional, so every record written before this field existed reads back as `undefined`,
     * which means "not removed" — the behaviour those records already had.
     */
    removed?: boolean;
    /**
     * When the edge acknowledged this upload (round 8, S3), so a restored project knows how old
     * each file is and can call it expired at `STUDIO_TTL_MS` without waiting for a 404. Optional:
     * a record written before this field reads back `undefined`, and the tab falls back to the
     * record's `savedAt`, which is never later than the upload.
     */
    uploadedAt?: number;
    /**
     * R3-JB (review 7): an animated image's length as its OWN BYTES timed it (`sniffAnimated`, JA-01), in ms. A fresh
     * load takes the clip's length from the bytes and lets nothing shorter re-time it (the edge's packed preview of
     * elephant.png plays 1.400 s for 1.417 s of frames); a restore has no bytes, so without this the preview body
     * re-timed it and the same project exported 1.433 s fresh and 1.400 s after Back. Optional: absent for a still, a
     * video, bytes that could not time the whole file, and every record written before the field.
     */
    bytesTimedMs?: number;
    /**
     * WHAT THE SCHEDULE CALL NEEDS FOR THIS INPUT, snapshotted at upload time (S-44).
     *
     * A restored project could not export (P6 dry-run row 21): `createMergeFileUpload` reads each
     * input's `getResponse()` (the `OriginalInputFile` — location, name, storage type and the
     * upload JWT) and `getMetadata()` (the `ProcessorMetadata` — the upload host and the edge
     * endpoints the render host pulls the bytes from), and after a reload there is no
     * `UploadInProgressFile` to read them off. `uploadedFileId` alone cannot rebuild them: the
     * endpoints are server configuration and the token is opaque.
     *
     * So the two JSON objects the wire carries are kept here, as they were, and
     * `build-request.ts` re-creates the upload from them (`restoredUploadOf`). NO BYTES: both are
     * strings and URLs, and the "never bytes" spec still walks the serialised record. Optional, so
     * a record written before this field reads back at `v: 1` unchanged — and a ref WITHOUT it
     * after a reload is exactly the old `unhydrated` block, in the old words.
     */
    wire?: StoredClipWire;
}

/** The per-input half of the schedule form, exactly as `_buildFormData` will re-serialise it. */
export interface StoredClipWire {
    /** `UploadInProgressFile.getResponse()`: `originalInputFiles[i]` on the wire. */
    originalInputFile: OriginalInputFile;
    /** `UploadInProgressFile.getMetadata().processorMetadataList[0]`: `metadataList[i]` on the wire. */
    processorMetadata: ProcessorMetadata;
    /** The page's `uniqueProcessType`, which the merge card is minted under. */
    uniqueProcessType: string;
}

/**
 * The wire snapshot of a FINISHED upload, or null while the edge has not answered.
 *
 * Structural on purpose: it reads the two getters and one field, never the class, so a test can
 * hand it a plain object and the tab can hand it whatever `StudioUploads` publishes. The
 * `ProcessorMetadata` is copied field-for-field (`{...}`) rather than referenced, so a later
 * mutation of the live upload (a `postProcessMetadata` swap) cannot rewrite the record.
 */
export function storedClipWireOf(upload: {
    getResponse?: () => OriginalInputFile | null | undefined;
    getMetadata?: () => { processorMetadataList?: ProcessorMetadata[] } | null | undefined;
    uniqueProcessType?: string;
} | null | undefined): StoredClipWire | null {
    const response = upload?.getResponse?.();
    const processor = upload?.getMetadata?.()?.processorMetadataList?.[0];
    if (!response || !response.location || !processor || !processor.uploadDomain) return null;
    return {
        originalInputFile: { ...response },
        processorMetadata: { ...processor },
        uniqueProcessType: String(upload?.uniqueProcessType ?? ''),
    };
}

export interface StudioUiState {
    selectedClipId: string | null;
    scrollMs: number;
    /** Round 5 (L12): the timeline's px per second the user chose, or null/absent for the fit. */
    zoom?: number | null;
    /**
     * `FrameState.touched` — the one bit of the frame the wire has no field for (review I6).
     *
     * `doc.frame` is the frame; this says whether the USER chose it, which is what stops a
     * second photo from moving it back to the first photo's shape. It is persisted beside the
     * document and restored with it: `frameStateFor(doc, ui)` reads it, a save writes
     * `state.touched` back, and an undo restores both because both are in the snapshot.
     */
    frameTouched: boolean;
    /**
     * Has the user set a hold themselves? — controller ruling 1 (2026-09-13).
     *
     * The rule "one photo alone gets 8 s" has to be undone when a second photo arrives, and
     * `relaxSoloHold` (P5b, `views/hold-defaults.ts:61`) can only compare the VALUE, so it cannot
     * tell an untouched 8 000 ms from a deliberate one — which its own KDoc claims it can. The
     * ruling puts the answer here: a flag in the editor's own state, persisted with the project,
     * that `relaxSoloHold` is only CALLED when false. P5b's function is untouched.
     *
     * The OWNER latches it by DIFFING `holdMs` across a commit (P5b.7 §4 rule 11), which covers
     * the inspector's field, its presets, "Same for every photo" and the track's resize drag with
     * one rule. `onFeatureUsed('hold')` cannot be the signal: it is once-per-feature-per-session,
     * so the second hold change of a session raises nothing.
     *
     * Not on the wire — the composition has no such field and inventing one would put a UI flag
     * in a contract two server tiers validate. Optional, so a record written before this existed
     * reads back as `undefined`, which means "not touched", which is the safe answer (the frame's
     * twin latch above is required because it shipped with the record's first version).
     */
    holdTouched?: boolean;
    /**
     * `FrameState.backgroundTouched`: the user chose the background colour (the Frame control's
     * Background row). What stops the first clip's default (white for an image, black for a video,
     * `withFirstClipBackground`) from overwriting a colour picked on an empty project. Optional like
     * `holdTouched`: absent reads as "not chosen", and a record written before it existed already has
     * its clips, so the default -- which runs only on the FIRST placement -- never touches it.
     */
    backgroundTouched?: boolean;
    /**
     * Round 4 (G3, C5): the desktop side columns folded to a rail. Optional and UI-only -- not on
     * the wire, not in a history snapshot; a record from before reads back as "open".
     */
    filesCollapsed?: boolean;
    inspectorCollapsed?: boolean;
    /**
     * S3-03 (review 7): the file name the user TYPED in the export sheet -- absent until they type one, so the
     * default (X1-06: the first clip's name) keeps following the timeline. Came back as "my-video" before.
     */
    fileName?: string;
    /** The sanitised name the last accepted submission carried: a name-only change mints a new revision (X1). */
    submittedFileName?: string;
}

export interface StudioRecord {
    v: number;
    kind: 'project';
    projectId: string;
    pageSlug: string;
    doc: StudioDocument;
    clips: StoredClipRef[];
    ui: StudioUiState;
    savedAt: number;
    /**
     * Bumped on every save; a write only lands on ANOTHER writer's record when it is strictly
     * above it (two tabs, D23 -- and review6 BUG-15 for why "not lower" was not enough).
     *
     * ROUND 6 (SDE#3/QA#8): a COUNTER, not a clock. Rounds 1–5 wrote `Date.now()` here, so on one
     * machine the newer WRITE always had the higher number and `writeProject`'s conflict branch was
     * unreachable: two tabs of one project overwrote each other silently and both chips read
     * "Saved". The editor now carries `nextGeneration(stored)` = the stored one + 1, and a tab that
     * sees another writer's higher number stops (see `foreignWriteOf`).
     */
    generation: number;
    /** Round 6: which tab wrote this generation (`mintTabId`). Absent on records from before. */
    writer?: string;
    /**
     * The last `(signature, revision)` the export sheet actually submitted, so the revision
     * rule survives a reload (review I1). Absent on a project that has never been exported,
     * and on every record written before this field existed — both mean the same thing to
     * `nextRevision`, which is why no version bump is needed.
     */
    submission?: ExportSubmission;
    /** `STUDIO_RECORD_RULES` at write time. Absent = generation 1 (see the constant). */
    rules?: number;
    /**
     * IT1-W1 (sde S9): the export sheet's option choices -- the compression preset, the quality mode
     * -- as `ConvertOptionsContextV2.getSummary()` gives them. The record used to carry none: a user
     * who set 1080p / high quality, reloaded and exported got the defaults without being told. A ROOT
     * key on purpose (root keys survive `writeProject`'s spread); optional, so every record written
     * before it reads back unchanged and restores the defaults, which is what it did. Strings and
     * numbers only -- the summary is the option tree's own values, never bytes.
     */
    options?: Record<string, unknown>;
    /**
     * Round 6 (SDE#5): the `ProcessorMetadata` fields EVERY clip's wire shares (the upload host and
     * its twelve endpoints), stored once. `compactRecord` writes it and strips those fields from each
     * clip's `wire.processorMetadata`; `expandRecord` puts them back on read. Absent on a record
     * written before this field, which reads exactly as it did. A 30-clip record carried the same
     * ~560 B twelve-URL block 30 times (53 KB of a 72 KB record was `wire`).
     */
    shared?: { processorMetadata: Partial<ProcessorMetadata> };
}

/** The per-file half of a `ProcessorMetadata`; never shared, always kept on the clip. */
const PER_FILE_METADATA_KEYS: ReadonlySet<string> = new Set(['fileMetadata', 'postProcessMetadata']);

/**
 * Store the shared `ProcessorMetadata` fields once — round 6 (SDE#5). Pure; the input is not mutated.
 *
 * A field is shared when every clip that has a wire carries the same JSON for it. With one such
 * clip everything but the per-file keys is "shared" (a one-clip record still shrinks); with none
 * the record is returned as is. A clip's own copy is left with the per-file keys and any field that
 * differs (two upload hosts in one project keep both `uploadDomain`s where they belong).
 */
export function compactRecord(record: StudioRecord): StudioRecord {
    const wired = record.clips.filter((c) => c?.wire?.processorMetadata);
    if (wired.length === 0) return record;
    const first = wired[0].wire!.processorMetadata as unknown as Record<string, unknown>;
    const shared: Record<string, unknown> = {};
    for (const key of Object.keys(first)) {
        if (PER_FILE_METADATA_KEYS.has(key)) continue;
        const json = JSON.stringify(first[key]);
        if (wired.every((c) => JSON.stringify((c.wire!.processorMetadata as unknown as Record<string, unknown>)[key]) === json)) shared[key] = first[key];
    }
    if (Object.keys(shared).length === 0) return record;
    const clips = record.clips.map((c) => {
        if (!c?.wire?.processorMetadata) return c;
        const own: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(c.wire.processorMetadata as unknown as Record<string, unknown>)) {
            if (!(key in shared)) own[key] = value;
        }
        return { ...c, wire: { ...c.wire, processorMetadata: own as unknown as ProcessorMetadata } };
    });
    return { ...record, clips, shared: { processorMetadata: shared as Partial<ProcessorMetadata> } };
}

/** The inverse of `compactRecord`; a record without `shared` is returned as is (the old shape). */
export function expandRecord(record: StudioRecord): StudioRecord {
    const shared = record?.shared?.processorMetadata;
    if (!shared || typeof shared !== 'object') return record;
    const clips = record.clips.map((c) => {
        if (!c?.wire?.processorMetadata) return c;
        return { ...c, wire: { ...c.wire, processorMetadata: { ...shared, ...c.wire.processorMetadata } as ProcessorMetadata } };
    });
    const { shared: _shared, ...rest } = record;
    return { ...rest, clips };
}

/**
 * Bring a readable record forward to this bundle's defaults (round 5, L16). Pure; the input is
 * never mutated; a record already at `STUDIO_RECORD_RULES` is returned as-is.
 *
 * Generation 1 → 2: every VIDEO clip's `audio.mode: 'MUTE'` is lifted to the default (KEEP) —
 * the mode is dropped, a custom `volume` is kept, an `audio` left empty goes away. The record
 * cannot tell a round-3 auto-mute from a user's own mute, and the owner's ruling (ROUND5 §2 H3)
 * is that the sound comes back: a mute the user still wants is one click on the tile; a mute
 * nobody asked for is a silent export they cannot explain.
 */
export function migrateRecord(record: StudioRecord): StudioRecord {
    const rules = typeof record.rules === 'number' && Number.isFinite(record.rules) ? record.rules : 1;
    if (rules >= STUDIO_RECORD_RULES) return record;
    let doc = record.doc;
    if (rules < 2 && Array.isArray(doc?.clips)) {
        const clips = doc.clips.map((clip): VisualClip => {
            if (clip.kind !== 'VIDEO' || clip.audio?.mode !== 'MUTE') return clip;
            const { mode: _mode, ...rest } = clip.audio;
            const { audio: _audio, ...bare } = clip;
            return Object.keys(rest).length > 0 ? { ...bare, audio: rest as ClipAudio } : bare;
        });
        doc = { ...doc, clips };
    }
    // Generation 2 → 3 (round 8, O4): a project born before round 4 opened on a 0.5 s CROSS-FADE
    // (`emptyDocument` before 3cf51076) and kept it in its record; restored under a bundle whose
    // default is NONE, the header still read "Between clips · Cross-fade 0.5 s" for a boundary
    // nobody chose (the owner's project). Exactly the old default — CROSSFADE at 500 ms — is
    // lifted to NONE; the 500 stays on the object so the cross-fade is one click away, as
    // `DEFAULT_TRANSITION` keeps it. Any other type or length is the user's and is left alone.
    if (rules < 3 && doc?.transition?.type === 'CROSSFADE' && doc.transition.durationMs === 500) {
        doc = { ...doc, transition: { ...doc.transition, type: 'NONE' } };
    }
    return { ...record, doc, rules: STUDIO_RECORD_RULES };
}

/**
 * A per-tab id, minted once per editor instance -- round 6 (SDE#3). Random rather than a counter
 * so two tabs opened in the same millisecond cannot collide; never persisted beyond `writer`.
 */
export function mintTabId(): string {
    const rnd = typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function'
        ? Array.from(crypto.getRandomValues(new Uint8Array(6))).map((b) => b.toString(16).padStart(2, '0')).join('')
        : Math.random().toString(16).slice(2, 14);
    return `t${rnd}`;
}

/** The generation the next save of this record must carry: strictly above what is stored. */
export function nextGeneration(stored: { generation?: unknown } | null | undefined, own: number = 0): number {
    const theirs = typeof stored?.generation === 'number' && Number.isFinite(stored.generation) ? stored.generation : 0;
    return Math.max(theirs, own) + 1;
}

/** The raw storage key a project record lives under (`storage` events carry this). */
export function projectStorageKey(projectId: string): string {
    return rawKey(projectId);
}

/**
 * Read a `storage` event as ANOTHER tab's newer write of `projectId`, or null.
 *
 * The event fires only in the tabs that did not write (spec), so a record whose `writer` is not
 * this tab and whose `generation` is above what this tab last saved is exactly "edited in another
 * tab". A write by this tab, an older generation (a slow tab catching up) and a foreign version
 * all answer null: none of them is a reason to stop the user typing.
 */
export function foreignWriteOf(
    event: { key?: string | null; newValue?: string | null } | null | undefined,
    projectId: string,
    tabId: string,
    ownGeneration: number,
): StudioRecord | null {
    if (!event || event.key !== rawKey(projectId) || !event.newValue) return null;
    let value: unknown;
    try { value = JSON.parse(event.newValue); } catch { return null; }
    if (!isRecord(value) || value.v !== STUDIO_RECORD_VERSION) return null;
    if (value.writer === tabId) return null;
    if (!(typeof value.generation === 'number' && value.generation > ownGeneration)) return null;
    return expandRecord(value);
}

/**
 * R14-S22 (QA R14-Q29): two records carry the SAME document -- structurally, whatever key order a JSON round trip or a
 * spread left behind, `undefined` members ignored. A foreign write whose `doc` equals what this tab holds (or last
 * saved) is not an edit made elsewhere: the only news it can carry is in `clips` (the ids another tab's uploads landed)
 * and `ui`, so the receiving tab converges on it and ADOPTS its generation instead of going stale -- QA's E1 had tab B
 * read "Not saved in this browser" for the rest of its life over a write that changed nothing it could see. Anything
 * else -- a clip trimmed, added or removed over there -- stays `becomeStale`'s case.
 */
export function sameDocument(a: unknown, b: unknown): boolean {
    return stableJson(a) === stableJson(b);
}

/**
 * R14-S22b (Sr review 5 #3): the refs of a same-document write differ from ours by FILLS ONLY -- each pair (by index) is
 * the same state (the same id, the same tombstone flag), or ours is an empty live ref and theirs carries an id (an upload
 * that landed over there), and anything theirs appended is theirs. A ref tombstoned over there that we hold live (a
 * tray-only file removed in the other tab: the document does not change, `order` is append-only), or an id regressed to
 * '' -- neither is "the ids landed", and both stay `becomeStale`'s case: adopting them wrote the removal back alive (and
 * the other tab adopted that), or drew "File gone" in a fresh tab.
 */
export function clipsDifferByFillsOnly(ours: readonly StoredClipRef[], theirs: readonly StoredClipRef[]): boolean {
    if (!Array.isArray(ours) || !Array.isArray(theirs) || theirs.length < ours.length) return false;
    for (let i = 0; i < ours.length; i += 1) {
        const mine = ours[i];
        const other = theirs[i];
        if (!mine || !other) return false;
        const mineId = mine.uploadedFileId || '';
        const otherId = other.uploadedFileId || '';
        const mineRemoved = mine.removed === true;
        const otherRemoved = other.removed === true;
        if (mineId === otherId && mineRemoved === otherRemoved) continue;
        if (mineId === '' && !mineRemoved && otherId !== '' && !otherRemoved) continue;
        return false;
    }
    return true;
}

function stableJson(value: unknown): string {
    if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'undefined';
    if (Array.isArray(value)) return `[${value.map((v) => stableJson(v)).join(',')}]`;
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj).filter((k) => obj[k] !== undefined).sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${stableJson(obj[k])}`).join(',')}}`;
}

/**
 * R14-S22 (QA R14-Q29): a tab that is STALE (another tab edited the document) has landed an upload for a slot the
 * shared record still holds EMPTY -- the third photo tab B took over in QA's E1 after A's hand-over. Its `save()` is
 * refused (a stale tab writes no document), so the id would be lost from the project for good. This writes the ids
 * ONLY: the stored record as it stands, each matching empty ref (by `clipId`, else by name + size -- the manager
 * re-mints the id in the taker) filled from `fills` under the STORED ref's `clipId` (the owner's slot id, which its
 * `convergeElsewhere` matches by), `generation` + 1, `writer` = the caller. Never shrinks the record, never touches
 * `doc` or `ui`. Null when there is no record or nothing to fill (a slot already filled, or none matching); otherwise
 * `writeProject`'s verdict with the record proposed.
 */
export function fillStoredRefs(
    projectId: string,
    fills: readonly StoredClipRef[],
    writer: string,
    now: number = Date.now(),
): { result: WriteResult; record: StudioRecord } | null {
    const stored = readProject(projectId, now);
    if (!stored || !Array.isArray(stored.clips) || fills.length === 0) return null;
    const clips = stored.clips.slice();
    const claimed = new Set<number>();
    const open = (i: number): boolean => { const r = clips[i]; return !!r && !claimed.has(i) && !r.uploadedFileId && r.removed !== true; };
    let filled = 0;
    for (const fill of fills) {
        if (!fill?.uploadedFileId) continue;
        let at = clips.findIndex((r, i) => open(i) && r.clipId === fill.clipId);
        if (at < 0) at = clips.findIndex((r, i) => open(i) && !!fill.name && r.name === fill.name && (!r.size || !fill.size || r.size === fill.size));
        if (at < 0) continue;
        claimed.add(at);
        clips[at] = { ...fill, clipId: clips[at].clipId };
        filled += 1;
    }
    if (filled === 0) return null;
    const record: StudioRecord = { ...stored, clips, savedAt: now, generation: nextGeneration(stored), writer };
    return { result: writeProject(record), record };
}

export interface StudioIndexEntry {
    projectId: string;
    pageSlug: string;
    savedAt: number;
    clips: number;
}

/**
 * `stale` (S3-06, review 7): ANOTHER TAB saved a newer version of this project; this tab stopped writing. It is not
 * `disabled` ("Not saved in this browser — don't close this tab"), which told the user to stay while the banner beside
 * it told them to reload -- the project IS saved, by the other tab, and reloading shows it.
 */
export type SaveState = 'saved' | 'saving' | 'disabled' | 'stale';

export type WriteResult =
    | { status: 'saved' }
    | { status: 'disabled' }
    | { status: 'conflict'; stored: StudioRecord }
    /** A record written by a NEWER bundle is left exactly where it is (D13). */
    | { status: 'foreign-version'; version: number };

function storage(): StorageService | null {
    if (typeof window === 'undefined') return null;
    try {
        return container.get(StorageService);
    } catch {
        return null;
    }
}

function rawKey(key: string): string {
    return `${STUDIO_PREFIX}-${key}`;
}

/** Write, READ BACK, compare, clean up. The read-back is the whole point (see the header). */
export function storageHealth(): 'ok' | 'disabled' {
    if (typeof window === 'undefined') return 'disabled';
    try {
        const stamp = `probe-${Date.now()}`;
        window.localStorage.setItem(PROBE_KEY, stamp);
        const back = window.localStorage.getItem(PROBE_KEY);
        window.localStorage.removeItem(PROBE_KEY);
        return back === stamp ? 'ok' : 'disabled';
    } catch {
        return 'disabled';
    }
}

function readRaw(key: string): unknown {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.localStorage.getItem(rawKey(key));
        return raw === null || raw === undefined ? null : JSON.parse(raw);
    } catch {
        // A corrupted entry is skipped, never fatal: its neighbours still read.
        return null;
    }
}

/**
 * `StorageService` is the writer; this function is the honesty.
 *
 * `store` swallows its own failure (`storage.service.ts:50-53`), so the only way to know
 * whether anything landed is to look — and looking for PRESENCE is not enough either: a
 * truncated or evicted value is present and wrong. What comes back has to be identical to
 * what went in, which is what `false` here reports to every caller as `disabled`.
 */
function writeRaw(key: string, value: unknown): boolean {
    const store = storage();
    if (!store) return false;
    let expected: string;
    try {
        expected = JSON.stringify(value);
    } catch {
        return false;
    }
    try {
        store.store(STUDIO_PREFIX, key, value);
        return window.localStorage.getItem(rawKey(key)) === expected;
    } catch {
        return false;
    }
}

function removeRaw(key: string): void {
    const store = storage();
    try {
        if (store) store.remove(STUDIO_PREFIX, key);
        else window.localStorage.removeItem(rawKey(key));
    } catch {
        /* removing is best-effort; a leftover key ages out on the next sweep */
    }
}

function isRecord(value: unknown): value is StudioRecord {
    if (!value || typeof value !== 'object') return false;
    const r = value as Record<string, unknown>;
    return r.kind === 'project' && typeof r.projectId === 'string' && typeof r.doc === 'object' && r.doc !== null;
}

/**
 * The stored record whatever its version — the only reader that can SEE a foreign one.
 *
 * `readProject` deliberately cannot (6.4: "unknown/newer record version -> absent"), and that
 * is exactly why `writeProject` needs this one: a writer that could not see a newer record
 * would treat its key as empty ground and overwrite it, taking the unknown root keys the
 * design asks to preserve with it (D13).
 */
function readAnyVersion(projectId: string): StudioRecord | null {
    const value = readRaw(projectId);
    return isRecord(value) ? value : null;
}

/**
 * `null` for absent, malformed, reserved, expired, or a version this bundle does not know (6.4).
 *
 * IT ENFORCES THE TTL ITSELF (review M11). The TTL rule (`STUDIO_TTL_MS`, 2.5 h) used to live only in `sweepExpired`,
 * so a caller that forgot to sweep first read back a project whose uploads the edge deleted
 * hours ago — a timeline of clips that every rendition call answers 404 for, restored as if it
 * were fine. The sweep is still what DELETES (this reads, it never writes); what changed is
 * that a caller can no longer get an expired record by skipping it. `now` is injectable for
 * the same reason `sweepExpired`'s is.
 */
export function readProject(projectId: string, now: number = Date.now()): StudioRecord | null {
    if (!isValidProjectId(projectId)) return null;
    const value = readAnyVersion(projectId);
    if (!value) return null;
    if (value.v !== STUDIO_RECORD_VERSION) return null;
    // A record with no readable `savedAt` is not aged out here: `sweepExpired` deletes it as
    // undatable, and a reader that hid it would leave a key nothing could explain.
    if (typeof value.savedAt === 'number' && now - value.savedAt >= STUDIO_TTL_MS) return null;
    return expandRecord(value);
}

/**
 * The last submission as the record remembers it, or null — `touchForExport`'s `last`.
 *
 * Validated rather than cast: the record is JSON from a store any extension can write, and a
 * junk `revision` fed into `Math.max(doc.revision, last.revision) + 1` would produce a
 * revision `IdempotentScheduleKey.forProjectRevision` throws on (a 500, not a 409). A shape
 * this cannot read is `null`, which is the pre-export behaviour: replay-safe, never a bump.
 */
export function storedSubmission(record: StudioRecord | null | undefined): ExportSubmission | null {
    const submission = record?.submission as Partial<ExportSubmission> | undefined;
    if (!submission || typeof submission !== 'object') return null;
    const { signature, revision } = submission;
    if (typeof signature !== 'string' || signature.length === 0) return null;
    if (typeof revision !== 'number' || !Number.isFinite(revision) || revision < 1) return null;
    return { signature, revision: Math.trunc(revision) };
}

/**
 * Save, unless another tab already holds this project at this generation or a newer one.
 *
 * Read-before-write rather than last-writer-wins: two tabs of one project is a real case
 * (6.4, D23), and silently discarding the tab the user is actually typing in is the worst
 * available outcome. The caller gets the stored record back and decides. A record from a
 * NEWER bundle is refused outright — `foreign-version`, nothing written.
 */
export function writeProject(rec: StudioRecord): WriteResult {
    // `isValidProjectId` already excludes the reserved spellings; the second call is the
    // second of D14's three guards, kept because the failure it prevents is silent.
    if (!isValidProjectId(rec.projectId) || isReservedProjectId(rec.projectId)) return { status: 'disabled' };
    const stored = readAnyVersion(rec.projectId);
    // A newer bundle's record is not empty ground. Leave it alone and say so, rather than
    // overwriting a shape this bundle cannot read (D13, review M1).
    if (stored && stored.v !== STUDIO_RECORD_VERSION) {
        return { status: 'foreign-version', version: typeof stored.v === 'number' ? stored.v : 0 };
    }
    const existing = stored;
    // COMPARE AND SET, not "newer wins" (review6 BUG-15). Two tabs that opened the same project
    // opened it at the same generation, so both propose the same next one -- `nextGeneration`
    // counts from the tab's own last save, not from the store, and the store is only read here.
    // With a strict `>` the second proposal of generation 6 was not "older", so it landed and
    // took the other tab's document with it: the last-write-wins this counter exists to prevent.
    // A write may therefore only step on a record ANOTHER writer left if it is strictly above it.
    // Its own (same `writer`) it may re-write at the same generation, because that is the
    // autosave retrying the snapshot an attempt failed on (`createAutosave`, N3), not a race.
    // An anonymous record (written before `writer` existed) is treated as another tab's: a tab
    // that adopted the stored generation on load always proposes one above it, so the rule costs
    // a live tab nothing and protects a record it cannot prove it wrote.
    const sameWriter = existing != null && typeof existing.writer === 'string' && existing.writer === rec.writer;
    if (existing && (sameWriter ? existing.generation > rec.generation : existing.generation >= rec.generation)) {
        return { status: 'conflict', stored: existing };
    }
    // Spread what was there first, so a key a newer bundle wrote at the ROOT survives this
    // save. Keys inside `doc` cannot survive -- `doc` is rebuilt from typed state (D13).
    // `shared` is rebuilt from THIS record's clips (SDE#5); a stale one from `existing` must not
    // survive the spread and describe clips it never saw.
    const { shared: _stale, ...base } = (existing ?? {}) as Partial<StudioRecord>;
    const merged = compactRecord({ ...base, ...rec, v: STUDIO_RECORD_VERSION, kind: 'project' as const } as StudioRecord);
    return writeRaw(rec.projectId, merged) ? { status: 'saved' } : { status: 'disabled' };
}

export function removeProject(projectId: string): void {
    if (!isValidProjectId(projectId)) return;
    removeRaw(projectId);
    const index = readIndex();
    const kept = index.filter((e) => e.projectId !== projectId);
    // Only when something actually left: a no-op rewrite is a wasted `setItem` on a store
    // that may be at quota, which is the one place a spare write can do harm (review M4).
    if (kept.length !== index.length) writeRaw(INDEX_KEY, kept);
}

export function readLatest(pageSlug: string): string | null {
    const value = readRaw(`${LATEST_PREFIX}${pageSlug}`);
    return typeof value === 'string' && isValidProjectId(value) ? value : null;
}

export function writeLatest(pageSlug: string, projectId: string): void {
    if (!isValidProjectId(projectId)) return;
    writeRaw(`${LATEST_PREFIX}${pageSlug}`, projectId);
}

/**
 * Remember that these uploads were still in flight when the page went away — round 6 (SDE#8).
 *
 * Written from `pagehide` (the only reliable moment), read ONCE on the next mount of the same page
 * (`takeInterruptedUploads` deletes it), so the note "Your files were still uploading — add them
 * again" appears exactly once, on return, and never again. Names only — never bytes (6.4). An
 * empty list clears the marker, so a pick that finished before the leave says nothing.
 */
export function writeInterruptedUploads(pageSlug: string, names: readonly string[], projectId?: string): void {
    const key = `${INTERRUPTED_PREFIX}${pageSlug}`;
    if (!names || names.length === 0) { removeRaw(key); return; }
    // R14-S18 (QA R14-Q24): the marker names the PROJECT the uploads belonged to, so a landing on another id
    // (an unknown `?sid=`, a /downloads link) does not read the previous project's cut-short files as its own.
    writeRaw(key, { names: names.slice(0, 50).map((n) => String(n)), at: Date.now(), ...(projectId ? { projectId } : {}) });
}

/**
 * The names left by the last leave of this page, and clears them. `[]` when there were none -- or, R14-S18
 * (R14-Q24), when the marker names a project and `projectId` is another: the marker is still consumed (the
 * sentence belongs to a landing that will never happen; the interrupted project's own record says "cut short"
 * on its rows when it is next opened), and nothing is said on the wrong project. A marker from before this
 * change (no `projectId`) is read as before.
 */
export function takeInterruptedUploads(pageSlug: string, projectId?: string): string[] {
    const key = `${INTERRUPTED_PREFIX}${pageSlug}`;
    const value = readRaw(key) as { names?: unknown; projectId?: unknown } | null;
    removeRaw(key);
    if (!value || typeof value !== 'object' || !Array.isArray(value.names)) return [];
    if (projectId && typeof value.projectId === 'string' && value.projectId !== projectId) return [];
    return value.names.filter((n): n is string => typeof n === 'string');
}

export function readIndex(): StudioIndexEntry[] {
    const value = readRaw(INDEX_KEY);
    if (!Array.isArray(value)) return [];
    return value.filter(
        (e): e is StudioIndexEntry =>
            !!e && typeof e === 'object' && typeof (e as StudioIndexEntry).projectId === 'string',
    );
}

/**
 * Move a project to the front of the index, evicting past five — and DELETING what it
 * evicted, because an index that forgot a project while its document stayed behind would
 * leak the largest thing this module stores.
 */
export function touchIndex(entry: StudioIndexEntry): StudioIndexEntry[] {
    const index = readIndex();
    const rest = index.filter((e) => e.projectId !== entry.projectId);
    const next = [entry, ...rest];
    const kept = next.slice(0, MAX_INDEXED_PROJECTS);
    for (const dropped of next.slice(MAX_INDEXED_PROJECTS)) removeRaw(dropped.projectId);
    // Only when something actually moved — `removeProject` already reasons this way (review
    // M4, M16): a no-op rewrite is a wasted `setItem` on a store that may be at quota, which
    // is the one place a spare write can do harm. An autosave touches this key every 750 ms.
    if (!sameIndex(index, kept)) writeRaw(INDEX_KEY, kept);
    return kept;
}

function sameIndex(a: readonly StudioIndexEntry[], b: readonly StudioIndexEntry[]): boolean {
    return a.length === b.length && a.every((e, i) => (
        e.projectId === b[i].projectId && e.pageSlug === b[i].pageSlug
        && e.savedAt === b[i].savedAt && e.clips === b[i].clips
    ));
}

/** Every key this origin holds, read by index so a concurrent delete cannot skip one. */
function allStorageKeys(): string[] {
    const keys: string[] = [];
    try {
        for (let i = 0; i < window.localStorage.length; i += 1) {
            const key = window.localStorage.key(i);
            if (key !== null) keys.push(key);
        }
    } catch {
        return [];
    }
    return keys;
}

/**
 * Delete every project record older than the TTL (`STUDIO_TTL_MS`, 2.5 h), and every record too broken to date.
 *
 * Scans the namespace rather than trusting the index: a record whose index entry was lost (an
 * interrupted eviction, a cleared index) would otherwise sit there until the origin was
 * cleared. The two bookkeeping keys are skipped by name, and every other feature's keys are
 * out of scope because the scan is prefixed.
 *
 * @returns the project ids it removed, for the caller's log line.
 */
export function sweepExpired(now: number = Date.now()): string[] {
    if (typeof window === 'undefined') return [];
    const removed: string[] = [];
    for (const key of allStorageKeys()) {
        if (!key.startsWith(`${STUDIO_PREFIX}-`)) continue;
        const suffix = key.slice(STUDIO_PREFIX.length + 1);
        if (suffix === INDEX_KEY || suffix.startsWith(LATEST_PREFIX) || suffix.startsWith(INTERRUPTED_PREFIX)) continue;
        const value = readRaw(suffix);
        const savedAt = isRecord(value) && typeof value.savedAt === 'number' ? value.savedAt : null;
        if (savedAt !== null && now - savedAt < STUDIO_TTL_MS) continue;
        removeRaw(suffix);
        removed.push(suffix);
    }
    if (removed.length > 0) {
        writeRaw(INDEX_KEY, readIndex().filter((e) => !removed.includes(e.projectId)));
    }
    return removed;
}

export interface Autosave {
    /** Queue a save. A no-op once autosave has backed off; see `createAutosave`. */
    schedule(rec: StudioRecord): void;
    /**
     * An EXPLICIT save attempt: ignores the back-off, and a success lifts it. Retries the last
     * snapshot when nothing has been edited since — so a "Saving is off — retry" control works
     * without the user having to type first.
     */
    flush(): void;
    /** What the header chip should say. `'disabled'` while backed off. */
    status(): SaveState;
    stop(): void;
}

/**
 * Debounced saving, flushed on `pagehide` — 6.2's "debounced 750 ms + `pagehide`".
 *
 * `pagehide` and not `beforeunload`: `beforeunload` does not fire on iOS Safari and is what
 * disqualifies a page from the back/forward cache. Never rely on bfcache and never close the
 * falcon socket (owner ruling) — this listener does neither.
 *
 * `onState` reports `saving` the moment an edit is scheduled and the write's own verdict when
 * it lands, so the header chip says what is true rather than what was intended. A `conflict`
 * (and a `foreign-version`) is reported as `disabled` for the chip's purposes — the user's
 * edit is not on disk — and the caller that wants to reconcile reads `WriteResult` from its
 * own `write`.
 *
 * IT BACKS OFF (review I3). A `disabled` verdict means the store refused the write, and
 * `StorageService.store` logs a `console.error` every time it does (`storage.service.ts:51`,
 * and the exception is thrown inside it, so this module cannot catch it any earlier). Without
 * a latch that is one `DOMException` and one console line PER 750 ms OF TYPING, forever, in
 * exactly the browser that can least afford it. So after a `disabled` the autosave stops
 * arming its timer: `schedule` keeps the latest record and reports `disabled`, and nothing is
 * attempted until an explicit `flush()` succeeds or the page reloads. One store attempt, one
 * console line, at the moment saving goes off. A `conflict` does NOT latch — it is another
 * tab being newer, not a storage failure.
 *
 * AND THE RETRY HAS SOMETHING TO WRITE (review N3). A failed attempt keeps the snapshot it
 * failed on, so `flush()` re-attempts THAT record when nothing has been edited since.
 * Otherwise a chip that offered "retry" would be a no-op until the user typed again, which is
 * the opposite of what a user who has just closed another tab to free space expects.
 */
export function createAutosave(
    write: (rec: StudioRecord) => WriteResult,
    debounceMs: number = AUTOSAVE_DEBOUNCE_MS,
    onState?: (state: SaveState) => void,
): Autosave {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let pending: StudioRecord | null = null;
    let stopped = false;
    /** The back-off latch. Set only by a storage `disabled`, lifted only by a `flush` that saves. */
    let backedOff = false;
    let state: SaveState = 'saved';
    /** The snapshot the last attempt failed on — what an explicit `flush()` retries (N3). */
    let unsaved: StudioRecord | null = null;

    // IT2-Y1b (sde N7): reported on a CHANGE only. `schedule` runs per coalesced commit -- at 60 Hz
    // during a drag -- and each `report('saving')` was a `setState` on the controller beside the
    // commit's own render; the chip already says "saving", so the second render said nothing new.
    const report = (next: SaveState): void => {
        if (next === state) return;
        state = next;
        if (onState) onState(next);
    };

    const clearTimer = (): void => {
        if (timer !== null) {
            clearTimeout(timer);
            timer = null;
        }
    };

    /** The one place `write` is called. Everything else decides WHETHER to get here. */
    const attempt = (rec: StudioRecord): void => {
        const result = write(rec);
        if (result.status === 'saved') {
            backedOff = false;
            unsaved = null;
            report('saved');
            return;
        }
        // Hold on to what did not land, so `flush()` has something to retry.
        unsaved = rec;
        if (result.status === 'disabled') backedOff = true;
        report('disabled');
    };

    /** The debounce and `pagehide` path: respects the latch, so it cannot become a hot loop. */
    const run = (): void => {
        clearTimer();
        if (!pending || stopped || backedOff) return;
        const rec = pending;
        pending = null;
        attempt(rec);
    };

    const onPageHide = (): void => run();
    if (typeof window !== 'undefined') window.addEventListener('pagehide', onPageHide);

    return {
        schedule(rec: StudioRecord): void {
            if (stopped) return;
            pending = rec;
            if (backedOff) {
                // Keep the record so an explicit flush has the latest, but arm nothing.
                report('disabled');
                return;
            }
            report('saving');
            clearTimer();
            timer = setTimeout(run, debounceMs);
        },
        flush(): void {
            clearTimer();
            if (stopped) return;
            // The queued edit if there is one, otherwise the snapshot that failed last.
            const rec = pending ?? unsaved;
            if (!rec) return;
            pending = null;
            attempt(rec);
        },
        status(): SaveState {
            return state;
        },
        stop(): void {
            stopped = true;
            clearTimer();
            pending = null;
            unsaved = null;
            if (typeof window !== 'undefined') window.removeEventListener('pagehide', onPageHide);
        },
    };
}
